import React, { PropsWithChildren, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { useFocusOnRefreshWithTabIndex, useRefreshBlock } from '../../BlockFetcher';
import { Heading, HeadingHierarchy } from '../Heading/Heading';
import { PostsInteractions } from './partials/PostInteractions';
import { PostDeleteModal } from './forms/PostDeleteModal';
import { PostFlagInappropriateModal } from './forms/PostFlagInappropriateModal';
import { PostBody } from './partials/PostBody';
import {
  isDeleteForm,
  isFlagInappropriateForm,
  isPostFirst,
  PostForm,
  PostFirst,
  PostReply,
  PostTypes,
  isUpdateForm,
} from './Post.types';

export type PostProps = {
  post: PostFirst | PostReply;
  headingHierarchy: HeadingHierarchy;
  previousPostId: string | undefined;
  type: PostTypes;
  replyEditorId?: string;
  currentForm: PostForm | undefined;
  setCurrentForm: (newState: PostForm | undefined) => void;
  postNumber: number | string; // can be 4 or '4.2',
};

export function Post({
  headingHierarchy,
  previousPostId,
  children,
  post,
  currentForm,
  setCurrentForm,
  replyEditorId,
  type,
  postNumber,
}: PropsWithChildren<PostProps>) {
  const { t } = useSonTranslation('Posts');
  const { hash } = useLocation();
  const { refresh, refreshCount, refreshProps } = useRefreshBlock();
  const focusRef = useFocusOnRefreshWithTabIndex<HTMLDivElement>(refreshCount);

  const closeModal = () => setCurrentForm(undefined);

  const { id } = post;

  /**
   * Ids for editor containers toggled via skip links (edit and reply "buttons")
   * */
  const editEditorId = `edit-editor-${id}`;

  /**
   * When update editor of a reply is replaced by reply editor from post.
   * We set currentForm to undefined to make sure, edit button is shown
   * */
  useEffect(() => {
    if (!hash.includes(id) && currentForm === post?.updateForm) {
      setCurrentForm(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  return (
    <>
      <div className="posts__post" id={id} data-type={type}>
        <Heading
          id={`post-heading-${id}`}
          className="invisible"
          hierarchy={headingHierarchy}
          ref={id === refreshProps ? focusRef : null}
        >
          {t(`default.heading.post.${type}`, { number: postNumber })}
        </Heading>
        <div className="posts__body-interactions-container">
          <PostBody
            post={post}
            headingHierarchy={headingHierarchy}
            type={type}
            editEditorId={editEditorId}
            form={isUpdateForm(currentForm, post.updateForm) ? currentForm : undefined}
            hash={hash}
          />
          <PostsInteractions
            postNumber={postNumber}
            postId={id}
            replyForm={isPostFirst(post) ? post.replyForm : undefined}
            updateForm={post?.updateForm}
            deleteForm={post?.deleteForm}
            flagInappropriateForm={post?.flagInappropriateForm}
            setCurrentForm={setCurrentForm}
            currentForm={currentForm}
            type={type}
            editEditorId={editEditorId}
            replyEditorId={replyEditorId}
          />
        </div>
        {children}
      </div>
      {isDeleteForm(currentForm, post.deleteForm) && (
        <PostDeleteModal
          id={id}
          closeModal={closeModal}
          type={type}
          form={currentForm}
          refresh={() => {
            refresh(previousPostId);
          }}
        />
      )}
      {isFlagInappropriateForm(currentForm, post.flagInappropriateForm) && (
        <PostFlagInappropriateModal id={id} type={type} closeModal={closeModal} form={currentForm} />
      )}
    </>
  );
}
